<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { driver } from 'driver.js'
  import 'driver.js/dist/driver.css'
  import '@/assets/driverjs.css'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    forceShow?: boolean
  }>()

  const ONBOARDING_COMPLETED_KEY = 'document-templates-onboarding-completed'

  const { t } = useTranslation({
    en: {
      step1Title: 'New Feature: Create Your Own Templates',
      step1Description:
        'You can now create custom PDF templates directly in JUPUS. Click here to get started and build templates tailored to your needs.',
      step2Title: 'Re-upload Existing Templates',
      step2Description:
        'Already have templates? You can re-upload your existing PDF files and continue using them with enhanced features.',
      nextButton: 'Next',
      prevButton: 'Previous',
      doneButton: 'Done'
    },
    de: {
      step1Title: 'Neue Funktion: Eigene Vorlagen erstellen',
      step1Description:
        'Sie können jetzt benutzerdefinierte PDF-Vorlagen direkt in JUPUS erstellen. Klicken Sie hier, um loszulegen und Vorlagen nach Ihren Bedürfnissen zu erstellen.',
      step2Title: 'Bestehende Vorlagen erneut hochladen',
      step2Description:
        'Haben Sie bereits Vorlagen? Sie können Ihre vorhandenen PDF-Dateien erneut hochladen und sie mit erweiterten Funktionen weiter verwenden.',
      nextButton: 'Weiter',
      prevButton: 'Zurück',
      doneButton: 'Fertig'
    }
  })

  const markOnboardingAsCompleted = () => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
  }

  const hasCompletedOnboarding = () => {
    return !props.forceShow && localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
  }

  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '[data-cy="DocumentTemplatesCreateButton"]',
          popover: {
            title: t('step1Title'),
            description: t('step1Description'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '[data-cy="DocumentTemplatesCreateButton"]',
          popover: {
            title: t('step2Title'),
            description: t('step2Description'),
            side: 'bottom',
            align: 'start'
          }
        }
      ],
      onDestroyed: () => {
        markOnboardingAsCompleted()
      },
      onHighlightStarted: () => {
        const button = document.querySelector('[data-cy="DocumentTemplatesCreateButton"]')
        if (button) {
          button.addEventListener(
            'click',
            () => {
              driverObj.destroy()
            },
            { once: true }
          )
        }
      },
      nextBtnText: t('nextButton'),
      prevBtnText: t('prevButton'),
      doneBtnText: t('doneButton')
    })

    driverObj.drive()
  }

  onMounted(() => {
    if (hasCompletedOnboarding()) {
      return
    }

    // Wait for the button to be rendered
    setTimeout(() => {
      const button = document.querySelector('[data-cy="DocumentTemplatesCreateButton"]')
      if (button) {
        startTour()
      }
    }, 500)
  })

  defineExpose({
    startTour
  })
</script>

<template>
  <div></div>
</template>
